/**
 * NetKeep Terms of Use and Privacy Policy, shared by the mobile and web apps so both
 * always show the same text. Plain data only: each app renders it with its own UI.
 *
 * Before launch:
 *   1. Fill in OWNER (anything still in [brackets] is shown to users as a draft warning).
 *   2. Have a lawyer licensed in your state review both documents.
 *   3. Bump LEGAL_EFFECTIVE_DATE whenever the text changes.
 *
 * The privacy policy describes what the code actually does. If you add analytics, crash
 * reporting, accounts, ads or anything that sends user data off the device, update it first.
 */

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LegalDocument {
  slug: "terms" | "privacy";
  title: string;
  effectiveDate: string;
  /** Short plain-language summary shown above the full text. */
  summary: string;
  sections: LegalSection[];
}

/** Who operates NetKeep. Values in [brackets] are placeholders that must be replaced. */
export const OWNER = {
  /** Your legal name, or your company's legal name (e.g. "NetKeep LLC"). */
  name: "Vou Xiong",
  /** Address that receives legal and privacy questions. */
  contactEmail: "xiongvou@gmail.com",
  /** U.S. state whose law governs the terms. */
  governingState: "Washington",
  /** County and state where disputes are heard. */
  venue: "Spokane County, Washington",
} as const;

/** ISO date the current text took effect. */
export const LEGAL_EFFECTIVE_DATE = "2026-10-21";

/** OWNER fields still holding a [placeholder]. Empty means ready to publish. */
export function unfilledPlaceholders(): string[] {
  return Object.entries(OWNER)
    .filter(([, value]) => /\[[^\]]+\]/.test(value))
    .map(([key]) => key);
}

/** One line for the calculator results, next to the numbers people act on. */
export const SHORT_DISCLAIMER =
  "Estimates only, not tax or financial advice. Fees change; confirm with the platform before you price or sell.";

const owner = OWNER.name;

export const TERMS: LegalDocument = {
  slug: "terms",
  title: "Terms of Use",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  summary:
    "NetKeep is a free estimating tool. Its numbers can be wrong or out of date, it is not professional advice, and you are responsible for the decisions you make with it.",
  sections: [
    {
      heading: "1. Agreement",
      paragraphs: [
        `These Terms of Use ("Terms") are an agreement between you and ${owner} ("we", "us"), the operator of NetKeep, including the NetKeep mobile app, the NetKeep website and the NetKeep fee-data service (together, the "Service").`,
        "By using the Service you agree to these Terms. If you do not agree, do not use the Service.",
      ],
    },
    {
      heading: "2. What NetKeep does",
      paragraphs: [
        "NetKeep estimates the fees, payout and profit of a sale from the numbers you enter and from fee schedules we maintain for selling platforms and payment methods.",
        "It is a planning and estimating tool only. It does not sell anything, process payments, connect to your platform accounts or know the actual fees on your account.",
      ],
    },
    {
      heading: "3. Estimates only — fees can be wrong or out of date",
      paragraphs: [
        "Platform fees vary by category, seller level, store subscription, region, promotions and other factors, and platforms can change them at any time without telling us. The fee schedules in NetKeep are compiled by hand from public sources and may be incomplete, simplified, unverified or out of date.",
        "Each fee profile shows a verification status and, where available, a link to its source. \"Unverified\" means no one has confirmed the rates against the platform's current published fees. Even \"Verified\" profiles can be wrong or become outdated.",
        "Results are estimates, rounded as described in the app. They are not a quote, invoice or statement of what any platform will charge you or pay you.",
      ],
    },
    {
      heading: "4. Not professional advice",
      paragraphs: [
        "NetKeep does not provide tax, legal, accounting, financial or business advice. Sales tax figures only show how tax may affect fees; they are not a determination of what tax you owe, collect or must remit. Consult a qualified professional for advice about your situation.",
      ],
    },
    {
      heading: "5. Your responsibility",
      paragraphs: ["You are solely responsible for:"],
      bullets: [
        "checking the current fees with each platform and payment provider before you list, price or sell;",
        "the accuracy of the numbers you enter;",
        "your pricing, listing, purchasing, tax and business decisions, even if you made them using NetKeep.",
      ],
    },
    {
      heading: "6. No warranty",
      paragraphs: [
        "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\", WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED OR STATUTORY, INCLUDING ANY WARRANTIES OF ACCURACY, COMPLETENESS, TIMELINESS, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT.",
        "We do not warrant that fee data or results are accurate, complete or current, or that the Service will be available, uninterrupted, secure or error-free. Some jurisdictions do not allow the exclusion of implied warranties, so some of these exclusions may not apply to you.",
      ],
    },
    {
      heading: "7. Limitation of liability",
      paragraphs: [
        `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${owner.toUpperCase()} AND ITS OWNERS, EMPLOYEES AND CONTRIBUTORS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, SALES, DATA OR GOODWILL, ARISING FROM OR RELATED TO THE SERVICE OR THESE TERMS, EVEN IF WE WERE TOLD SUCH DAMAGES WERE POSSIBLE.`,
        "TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE IS LIMITED TO THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICE IN THE 12 MONTHS BEFORE THE CLAIM, OR (B) US$50.",
        "These limits apply to every theory of liability, including contract, tort (including negligence), strict liability and statute. Some jurisdictions do not allow these limitations, so they may not fully apply to you.",
      ],
    },
    {
      heading: "8. Other platforms and trademarks",
      paragraphs: [
        "NetKeep is independent. It is not affiliated with, endorsed by or sponsored by eBay, Mercari, Etsy, PayPal, Meta, or any marketplace or payment provider. Their names and trademarks belong to their owners and are used only to identify whose fees an estimate refers to.",
        "Links to other websites are provided for convenience. We do not control those sites and are not responsible for their content, accuracy or practices.",
      ],
    },
    {
      heading: "9. Acceptable use",
      paragraphs: ["You agree not to:"],
      bullets: [
        "use the Service for anything unlawful;",
        "overload, disrupt or try to gain unauthorized access to the Service or its servers, including by bypassing rate limits;",
        "access the administrative parts of the Service without our permission;",
        "present NetKeep results to others as official fees of any platform.",
      ],
    },
    {
      heading: "10. Changes and availability",
      paragraphs: [
        "We may change, suspend or stop the Service, or any feature or fee profile, at any time. We may update these Terms; the effective date at the top will change, and continuing to use the Service after an update means you accept the updated Terms.",
      ],
    },
    {
      heading: "11. Governing law and disputes",
      paragraphs: [
        `These Terms are governed by the laws of the State of ${OWNER.governingState}, USA, without regard to its conflict-of-laws rules. Any dispute related to the Service or these Terms will be brought only in the state or federal courts located in ${OWNER.venue}, and you and we consent to their jurisdiction. Nothing in these Terms limits rights you have under consumer-protection laws that cannot be waived by contract.`,
      ],
    },
    {
      heading: "12. General",
      paragraphs: [
        "If any part of these Terms is found unenforceable, the rest remains in effect. Our not enforcing a provision is not a waiver of it. These Terms, together with the Privacy Policy, are the entire agreement between you and us about the Service.",
      ],
    },
    {
      heading: "13. Contact",
      paragraphs: [`Questions about these Terms: ${OWNER.contactEmail}.`],
    },
  ],
};

export const PRIVACY: LegalDocument = {
  slug: "privacy",
  title: "Privacy Policy",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  summary:
    "No account, no ads, no analytics, no tracking. Your calculations stay on your device. Our server only receives requests for fee data, which include your IP address.",
  sections: [
    {
      heading: "1. Who we are",
      paragraphs: [
        `This policy explains how ${owner} ("we", "us") handles information in the NetKeep mobile app, the NetKeep website and the NetKeep fee-data service (the "Service").`,
      ],
    },
    {
      heading: "2. Information that stays on your device",
      paragraphs: [
        "Everything you type into NetKeep — sale prices, costs, fees, tax settings — is calculated on your device and is never sent to us. The following is stored only on your device (in the app's local database, or in your browser's local storage on the website):",
      ],
      bullets: [
        "calculations you choose to save;",
        "a copy of the most recent fee data, so the app works offline;",
        "your settings, such as the color theme.",
      ],
    },
    {
      heading: "3. Information our server receives",
      paragraphs: [
        "To download current fee data, the app contacts the NetKeep server. Like any web request, this sends technical information: your IP address, the date and time, the address requested, and your device's browser or app identifier (user agent).",
        "We use this information only to deliver fee data, to protect the Service from abuse (for example, rate limiting by IP address), and to diagnose problems. It is not linked to your name or to any calculations, and we do not use it to build a profile of you. Server logs are kept by our hosting provider for a limited period and then deleted.",
      ],
    },
    {
      heading: "4. What we do not do",
      paragraphs: ["We do not:"],
      bullets: [
        "require an account, or collect your name, email address, phone number or payment details;",
        "use analytics, advertising, tracking pixels or cross-site tracking;",
        "set cookies on the website;",
        "sell or share your personal information, as \"sell\" and \"share\" are defined in U.S. state privacy laws such as the California Consumer Privacy Act.",
      ],
    },
    {
      heading: "5. Service providers",
      paragraphs: [
        "Our server and website are hosted by a cloud provider (currently Render), which processes the request information above on our behalf. App stores such as Google Play and the Apple App Store, and your device or browser, collect information under their own privacy policies, which we do not control.",
        "We may disclose information if required by law, or to protect the rights and safety of users, the public or the Service.",
      ],
    },
    {
      heading: "6. Your choices",
      paragraphs: [],
      bullets: [
        "Delete saved calculations at any time in Settings (one at a time on the Saved screen, or all at once).",
        "On the website, clearing this site's data in your browser removes everything NetKeep stored there.",
        "Uninstalling the mobile app removes its data from your device.",
        "Because we do not hold your calculations or link server logs to you, we generally have no personal information to give you or delete. You can still contact us with any privacy request.",
      ],
    },
    {
      heading: "7. Security",
      paragraphs: [
        "Fee data is sent over HTTPS when the server supports it. Data stored on your device is protected by your device's own security; anyone with access to your unlocked device or browser profile can see your saved calculations.",
      ],
    },
    {
      heading: "8. Children",
      paragraphs: [
        "NetKeep is not directed to children under 13, and we do not knowingly collect personal information from children.",
      ],
    },
    {
      heading: "9. Changes",
      paragraphs: [
        "If we change how the Service handles information, we will update this policy and its effective date before the change takes effect.",
      ],
    },
    {
      heading: "10. Contact",
      paragraphs: [`Privacy questions or requests: ${OWNER.contactEmail}.`],
    },
  ],
};

export const LEGAL_DOCUMENTS = [TERMS, PRIVACY] as const;
